import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { COUNTRY_NAMES, UI, tr } from "@/i18n";
import { useLang } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LeadCard = ({ lead, statuses, token, reload, lang }) => {
  const a = (lead.assessments || [])[0];
  const [notes, setNotes] = useState(lead.internalNotes || "");
  const [nextAction, setNextAction] = useState(lead.nextAction || "");
  const [open, setOpen] = useState(false);
  const hdr = { headers: { Authorization: `Bearer ${token}` } };

  const patch = async (body) => {
    await axios.patch(`${API}/admin/onematch/leads/${lead.id}`, body, hdr);
    reload();
  };
  const save = async () => { await patch({ internalNotes: notes, nextAction }); toast.success(tr(lang, UI.admin_saved)); };
  const resend = async () => {
    const r = await axios.post(`${API}/admin/onematch/assessments/${a.id}/resend`, {}, hdr);
    toast[r.data.ok ? "success" : "error"](r.data.ok ? "Report resent" : `Failed: ${r.data.detail}`);
    reload();
  };
  const ctaClicks = (a && a.ctaClicks) || lead.ctaClicks || {};
  const ctaStr = Object.entries(ctaClicks).map(([k, v]) => `${k}:${v}`).join(" · ");

  return (
    <div className="bg-white border border-border rounded-sm p-5">
      <div className="flex flex-wrap justify-between gap-3">
        <div>
          <p className="font-display text-xl text-navy-deep">{lead.firstName} {lead.lastName}
            {lead.personalAssessmentRequested && <span className="ml-2 text-[10px] uppercase tracking-wide bg-gold/20 text-gold rounded-full px-2 py-0.5">consult</span>}
          </p>
          <p className="text-sm text-muted-foreground">{lead.email} · {lead.phone}</p>
          <p className="text-sm text-muted-foreground">Residence: {lead.country} · Nationality: {lead.nationality || "—"} · Lang: {lead.preferredLanguage || a?.lang || "—"} · Marketing: {String(lead.marketingConsent || false)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">Source: {lead.source || "—"} {lead.landingPageUrl ? `· ${lead.landingPageUrl}` : ""}</p>
        </div>
        <div className="text-right text-xs text-muted-foreground">
          <p>{new Date(lead.createdAt).toLocaleString()}</p>
          <p>Lead ID: {lead.id.slice(0, 8)}</p>
          {a && <p>Assessment: {a.id.slice(0, 8)}</p>}
        </div>
      </div>

      {a && (
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          {(a.topMatches || []).map((m) => (
            <span key={m.code} className="text-xs bg-navy text-white rounded-full px-3 py-1">{tr(lang, COUNTRY_NAMES[m.code]) || m.name} {m.overall}%</span>
          ))}
          <span className={`text-xs rounded-full px-3 py-1 ${a.emailStatus === "Sent" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{tr(lang, UI.admin_report_status)}: {a.emailStatus}</span>
          <span className="text-xs rounded-full px-3 py-1 bg-cream text-navy-deep border border-border">{tr(lang, UI.admin_purchase_status)}: {a.reportPurchaseStatus || "none"}</span>
          <span className="text-xs rounded-full px-3 py-1 bg-cream text-navy-deep border border-border">{tr(lang, UI.admin_consult_status)}: {a.consultationStatus || "none"}</span>
        </div>
      )}

      {ctaStr && <p className="mt-2 text-xs text-muted-foreground">{tr(lang, UI.admin_cta_clicks)}: {ctaStr}</p>}

      <div className="mt-4 grid sm:grid-cols-2 gap-3">
        <div>
          <label className="text-xs font-medium text-navy-deep">{tr(lang, UI.admin_funnel)}</label>
          <select data-testid={`status-${lead.id}`} value={lead.status} onChange={(e) => patch({ status: e.target.value })} className="mt-1 w-full h-9 border border-input rounded-sm px-2 text-sm">
            {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-medium text-navy-deep">{tr(lang, UI.admin_next_action)}</label>
          <input data-testid={`next-${lead.id}`} value={nextAction} onChange={(e) => setNextAction(e.target.value)} className="mt-1 w-full h-9 border border-input rounded-sm px-2 text-sm" />
        </div>
      </div>
      <div className="mt-3">
        <label className="text-xs font-medium text-navy-deep">{tr(lang, UI.admin_notes)}</label>
        <textarea data-testid={`notes-${lead.id}`} value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1 w-full border border-input rounded-sm px-2 py-1.5 text-sm" />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button data-testid={`save-${lead.id}`} onClick={save} className="text-sm bg-navy text-white rounded-sm px-4 py-1.5 hover:bg-navy-deep">{tr(lang, UI.admin_save)}</button>
        {a && <button data-testid={`resend-${lead.id}`} onClick={resend} className="text-sm border border-border rounded-sm px-4 py-1.5 hover:border-navy">Resend Result Email</button>}
        {a && <a href={`/onematch/report/${a.reportToken}`} target="_blank" rel="noreferrer" className="text-sm text-navy underline">Open result</a>}
        {a && <button onClick={() => setOpen(!open)} className="text-sm text-navy underline">{open ? "Hide" : "View"} answers</button>}
      </div>
      {open && a && <pre className="mt-3 text-xs bg-cream border border-border rounded-sm p-3 overflow-x-auto max-h-72">{JSON.stringify(a.answers, null, 2)}</pre>}
    </div>
  );
};

const Admin = () => {
  const { lang } = useLang();
  const [token, setToken] = useState(localStorage.getItem("om_admin_jwt") || "");
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [data, setData] = useState({ leads: [], statuses: [] });
  const [q, setQ] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const load = useCallback(async (t) => {
    try {
      const r = await axios.get(`${API}/admin/onematch/leads`, { headers: { Authorization: `Bearer ${t}` } });
      setData(r.data); setAuthed(true);
    } catch (e) { setAuthed(false); localStorage.removeItem("om_admin_jwt"); }
  }, []);

  useEffect(() => { if (token) load(token); }, []); // eslint-disable-line

  const login = async () => {
    try {
      const r = await axios.post(`${API}/admin/login`, { email, password });
      const t = r.data.token;
      localStorage.setItem("om_admin_jwt", t); setToken(t); load(t);
    } catch (e) { toast.error(tr(lang, UI.admin_invalid)); }
  };
  const logout = () => { localStorage.removeItem("om_admin_jwt"); setToken(""); setAuthed(false); };

  if (!authed) {
    return (
      <div className="max-w-sm mx-auto px-5 py-20">
        <h1 className="font-display text-3xl text-navy-deep mb-6">{tr(lang, UI.admin_login_title)}</h1>
        <label className="text-sm font-medium text-navy-deep">{tr(lang, UI.admin_email)}</label>
        <input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-11 border border-input rounded-sm px-3 mb-3 mt-1" />
        <label className="text-sm font-medium text-navy-deep">{tr(lang, UI.admin_password)}</label>
        <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()} className="w-full h-11 border border-input rounded-sm px-3 mb-4 mt-1" />
        <button data-testid="admin-login" onClick={login} className="w-full bg-navy text-white py-3 rounded-sm font-semibold">{tr(lang, UI.admin_signin)}</button>
      </div>
    );
  }

  const leads = data.leads.filter((l) => {
    const s = `${l.firstName} ${l.lastName} ${l.email} ${l.phone}`.toLowerCase();
    return (!q || s.includes(q.toLowerCase())) && (!filterStatus || l.status === filterStatus);
  });

  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
      <div className="flex items-center justify-between mb-1">
        <h1 className="font-display text-4xl text-navy-deep">ONE MATCH Leads</h1>
        <button data-testid="admin-logout" onClick={logout} className="text-sm text-muted-foreground hover:text-navy underline">{tr(lang, UI.admin_signout)}</button>
      </div>
      <p className="text-muted-foreground mb-6">{leads.length} leads</p>
      <div className="flex flex-wrap gap-3 mb-6">
        <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name/email/phone" className="h-10 border border-input rounded-sm px-3 text-sm flex-1 min-w-[200px]" />
        <select data-testid="admin-filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="h-10 border border-input rounded-sm px-3 text-sm">
          <option value="">All statuses</option>
          {data.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="space-y-4" data-testid="admin-leads">
        {leads.map((l) => <LeadCard key={l.id} lead={l} statuses={data.statuses} token={token} reload={() => load(token)} lang={lang} />)}
      </div>
    </div>
  );
};

export default Admin;
